import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// mui components
import { Box, Stack, Typography } from "@mui/material";

// custom components
import CustomLoader from "../../components/loader";
import Body3 from "../../components/typography/body-03";
import Body2 from "../../components/typography/body-02";
import Heading2 from "../../components/typography/heading-02";
import Heading3 from "../../components/typography/heading-03";
import MessageCarousel from "../../components/message-carousel";

// custom icons
import LoganLogo from "../../assets/img/login-logo.png";

// api slice
import { useSetPasswordActionMutation } from "../../features/set-password/setPasswordApiSlice";

// custom styles
import styles from "./index.module.scss";

export default function SetPasswordPage() {
	const navigate = useNavigate();
	const [setPasswordAction, { isLoading }] = useSetPasswordActionMutation();

	const [systemMessages, setSystemMessages] = useState([]);

	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState("");

	let { id } = useParams();
	console.log("22 id:", id);

	const handleSetPasswordClick = async (e) => {
		e.preventDefault();

		if (password !== confirmPassword) {
			setError("Passwords do not match.");
		} else {
			setError("");

			// dispatch api
			try {
				const userData = await setPasswordAction({
					uuid: id,
					password,
				}).unwrap();
				setError(userData.message || "Password reset successfully.");
				setSuccess(true);
			} catch (err) {
				setError(err?.data?.message || "An error occurred. Please try again.");
				setSuccess(false);
			}
		}
	};

	return (
		<Box className={styles.pageContainer}>
			<Box className={styles.bodyContainer}>
				<Box
					className={styles.bodyLeftContainer}
					sx={{
						display: { xs: "none", md: "flex" },
					}}
				>
					<Box className={styles.imageContainer}>
						<img
							src={LoganLogo}
							alt="logan logo"
							styles={{ width: "100%", height: "100%" }}
						/>
					</Box>
					<Stack
						flex={1}
						flexDirection="column"
						className={styles.welcomeContainer}
					>
						<Stack className={styles.contentContainer}>
							<Stack>
								<Heading3
									text="Welcome"
									fontType="bold"
									extraClass={styles.welcomeTitle}
								/>

								<Typography
									component="p"
									sx={{
										fontSize: "1.125rem",
										lineHeight: "2rem",
										fontWeight: 600,
										color: "#fff",
									}}
									className={styles.welcomeDescription}
								>
									Loan management made easy—submit, price, and lock your loans
									with real-time data and effortless control.
								</Typography>
							</Stack>

							<Stack className={styles.messageContainer}>
								<MessageCarousel data={systemMessages} />
							</Stack>
						</Stack>
					</Stack>
					<Stack className={styles.footerContainer} gap={1}>
						<Body3
							text="Please reach out to us if you are experiencing issues logging into the portal or resetting your password at:"
							fontType="semibold"
							extraClass={styles.footerTitle}
						/>
						<Stack flexDirection="row" className={styles.infoContainer}>
							<Body3 text="Email:" extraClass={styles.footerText} />
							<Body3
								text={
									<a
										href="mailto:tpoportalsupport@loganfinance.com"
										rel="noopener noreferrer"
										target="_top"
										className={styles.footerLink}
									>
										tpoportalsupport@loganfinance.com
									</a>
								}
							/>
						</Stack>
						<Stack flexDirection="row" className={styles.infoContainer}>
							<Body3 text="Phone:" extraClass={styles.footerText} />
							<Body3 text="910-302-5924" extraClass={styles.footerLink} />
						</Stack>
					</Stack>
				</Box>

				<Stack className={styles.bodyRightContainer}>
					<form
						onSubmit={handleSetPasswordClick}
						className={styles.formContainer}
					>
						<Stack className={styles.formBodyContainer}>
							<Heading2
								text="Create New Password"
								fontType="bold"
								extraClass={styles.setText}
							/>

							<Stack className={styles.formFieldsContainer}>
								<Box className={styles.inputFieldContainer}>
									<label htmlFor="password" className={styles.inputLabel}>
										Password
									</label>
									<input
										type="password"
										className={styles.inputField}
										id="password"
										aria-describedby="password"
										placeholder="Enter your password"
										value={password}
										onChange={(event) => {
											setPassword(event.target.value);
											setError("");
										}}
									/>
								</Box>

								<Box className={styles.inputFieldContainer}>
									<label
										htmlFor="confirm-password"
										className={styles.inputLabel}
									>
										Confirm Password
									</label>
									<input
										type="password"
										className={styles.inputField}
										id="confirm-password"
										aria-describedby="password"
										placeholder="Confirm new password"
										value={confirmPassword}
										onChange={(event) => {
											setConfirmPassword(event.target.value);
											setError("");
										}}
									/>
								</Box>
							</Stack>

							{error !== "" && <p style={{ color: "#f00" }}>{error}</p>}
							{!success && (
								<button
									type="submit"
									onClick={handleSetPasswordClick}
									className={styles.submitBtn}
								>
									{isLoading ? (
										<CustomLoader sx={{ color: "#fff" }} size={20} />
									) : (
										<Body3
											text="Set Password"
											fontType="semibold"
											extraClass={styles.submitText}
										/>
									)}
								</button>
							)}
						</Stack>

						{success && (
							<Stack className={styles.footerBodyContainer}>
								<Body2 text="Back to Login" extraClass={styles.loginText} />

								<Box
									onClick={() => navigate("/login")}
									className={styles.footerBtn}
								>
									<Body3
										text="Login"
										fontType="semibold"
										extraClass={styles.footerText}
									/>
								</Box>
							</Stack>
						)}
					</form>
				</Stack>
			</Box>
		</Box>
	);
}
